import { envConfig } from '../../config';
import { handleError } from '../generic/generic.utils';

const GEOAPIFY_AUTOCOMPLETE_TIMEOUT_MS = 4300;

export type GeoapifyPrediction = {
  properties: {
    place_id: string; // stable id from Geoapify
    formatted: string;
    name?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    country?: string;
    country_code?: string; // ISO-2
    postcode?: string; // zip / postal code
    street?: string;
    housenumber?: string;
    lon: number;
    lat: number;
    result_type?: string;
    // ...GeoJSON properties can include more; add as needed
  };
};

export type GeopifyLocationDetails = {
  placeId?: string;
  label: string; // human-readable label
  country?: string;
  countryCode?: string; // ISO-2
  state?: string;
  county?: string;
  city?: string; // city-like (city/town/village fallback)
  postcode?: string; // zip / postal code
  street?: string;
  houseNumber?: string;
  formatted?: string;
  lat: number;
  lon: number;
  resultType?: string;
};

type AutocompleteResponse = {
  features: Array<GeoapifyPrediction>;
};

type PlaceDetailsResponse = {
  features: Array<GeoapifyPrediction>;
};

type ReverseGeocodeResponse = AutocompleteResponse;

export type GeoapifyAutocompleteType =
  | 'country'
  | 'state'
  | 'city'
  | 'postcode'
  | 'street'
  | 'amenity'
  | 'locality';

export type AutocompleteOptions = {
  limit?: number;
  lang?: string;
  bias?: { lon: number; lat: number };
  filterRect?: { lon1: number; lat1: number; lon2: number; lat2: number };
  countryCode?: string;
  type?: GeoapifyAutocompleteType;
};

export class GeoapifyService {
  private readonly base = 'https://api.geoapify.com';
  private readonly key: string;

  constructor() {
    const apiKey = envConfig.REACT_APP_GEOAPIFY_API_KEY;
    if (!apiKey) throw new Error('Geoapify API key is required');
    this.key = apiKey;
  }

  /**
   * Get predictions (autocomplete) for a free-text query.
   */
  async autocomplete(
    searchQuery: string,
    opts: AutocompleteOptions = {},
    abortSignal?: AbortSignal
  ): Promise<GeopifyLocationDetails[]> {
    const params = new URLSearchParams({
      text: searchQuery,
      apiKey: this.key,
      limit: String(opts.limit ?? 8),
    });

    if (opts.lang) params.set('lang', opts.lang);
    else params.set('lang', 'en');

    if (opts.bias)
      params.set('bias', `point:${opts.bias.lon},${opts.bias.lat}`);

    if (opts.filterRect) {
      const { lon1, lat1, lon2, lat2 } = opts.filterRect;
      params.set('filter', `rect:${lon1},${lat1},${lon2},${lat2}`);
    }

    if (opts.countryCode) {
      params.set('filter', `countrycode:${opts.countryCode.toLowerCase()}`);
    }

    if (opts.type) {
      params.set('type', opts.type);
    }

    const url = `${this.base}/v1/geocode/autocomplete?${params.toString()}`;

    const controller = new AbortController();
    let removeAbortListener: (() => void) | undefined;
    if (abortSignal) {
      if (abortSignal.aborted) {
        controller.abort(abortSignal.reason);
      } else {
        const externalAbortHandler = () =>
          controller.abort(
            (abortSignal as { reason?: unknown }).reason ?? undefined
          );
        abortSignal.addEventListener('abort', externalAbortHandler, {
          once: true,
        });
        removeAbortListener = () =>
          abortSignal.removeEventListener('abort', externalAbortHandler);
      }
    }

    const fetchPromise = (async () => {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok)
        throw new Error(`Geoapify autocomplete failed: ${res.status}`);
      const data: AutocompleteResponse = await res.json();

      return data.features.map((f) => this.toDetails(f));
    })();

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let timedOut = false;
    const timeoutPromise = new Promise<undefined>((resolve) => {
      timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
        resolve(undefined);
      }, GEOAPIFY_AUTOCOMPLETE_TIMEOUT_MS);
    });

    const guardedFetchPromise = fetchPromise.catch((error) => {
      if (timedOut) {
        return undefined;
      }
      throw error;
    });

    try {
      const result = await Promise.race([guardedFetchPromise, timeoutPromise]);

      if (typeof result === 'undefined') {
        return [];
      }

      return result;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      removeAbortListener?.();
    }
  }

  /**
   * Get details for a selected location by placeId if available.
   * If you only have coordinates, pass { lat, lon } and it will reverse-geocode.
   */
  async getDetails(
    args:
      | { placeId: string }
      | { lat: number; lng: number; lang?: string }
      | { postcode: string; countryCode?: string; lang?: string },
    abortSignal?: AbortSignal
  ): Promise<GeopifyLocationDetails | null> {
    if ('placeId' in args) {
      const params = new URLSearchParams({
        apiKey: this.key,
        id: args.placeId,
        type: 'postcode',
        format: 'json',
      });
      const url = `${this.base}/v2/place-details?${params.toString()}`;
      const res = await fetch(url, { signal: abortSignal });
      if (!res.ok)
        throw new Error(`Geoapify place-details failed: ${res.status}`);

      const data: PlaceDetailsResponse = await res.json();
      const hit = data.features?.[0];
      if (!hit) return null;

      return this.toDetails(hit);
    }

    // Forward geocode by postcode (optionally scoped by country)
    if ('postcode' in args) {
      const params = new URLSearchParams({
        text: args.postcode,
        apiKey: this.key,
        limit: '1',
      });
      if (args.lang) params.set('lang', args.lang);
      // Narrow search to postcode results and country if provided
      params.set('type', 'postcode');
      if (args.countryCode) {
        params.set('filter', `countrycode:${args.countryCode.toLowerCase()}`);
      }

      const url = `${this.base}/v1/geocode/search?${params.toString()}`;
      const res = await fetch(url, { signal: abortSignal });
      if (!res.ok)
        throw new Error(`Geoapify search by postcode failed: ${res.status}`);
      const data: AutocompleteResponse = await res.json();
      const hit = data.features?.[0];
      if (!hit) return null;
      return this.toDetails(hit);
    }

    // Reverse geocode (coordinates only)
    const lang = args.lang ? `&lang=${encodeURIComponent(args.lang)}` : '';
    const url = `${this.base}/v1/geocode/reverse?lat=${args.lat}&lon=${args.lng}${lang}&apiKey=${this.key}`;
    const res = await fetch(url, { signal: abortSignal });
    if (!res.ok)
      throw new Error(`Geoapify reverse geocode failed: ${res.status}`);

    const data: ReverseGeocodeResponse = await res.json();
    const hit = data.features?.[0];
    if (!hit) return null;

    return this.toDetails(hit);
  }

  // --- helpers ---

  private toDetails({
    properties,
  }: GeoapifyPrediction): GeopifyLocationDetails {
    return {
      placeId: properties.place_id,
      label: properties.formatted ?? properties.name ?? '',
      country: properties.country,
      countryCode: properties.country_code?.toUpperCase(),
      state: properties.state,
      county: properties.county,
      city: properties.city ?? properties.town ?? properties.village,
      postcode: properties.postcode,
      street: properties.street,
      houseNumber: properties.housenumber,
      formatted: properties.formatted,
      lat: properties.lat,
      lon: properties.lon,
      resultType: properties.result_type,
    };
  }
}

export async function geopifyGetPredictions(
  service: GeoapifyService,
  query: string,
  opts: AutocompleteOptions = {}
): Promise<GeopifyLocationDetails[] | undefined> {
  const abortController = new AbortController();
  try {
    const data = await service.autocomplete(
      query,
      opts,
      abortController.signal
    );
    return data;
  } catch (e) {
    if ((e as any)?.name === 'AbortError') return;
    handleError(e);
  }
}
